import { useEffect, useState } from "react";
import { api, errDetail, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { GitCompareArrows, Plus, Trash2, Sparkles, RefreshCw } from "lucide-react";

const emptyOffer = () => ({ supplier: "", unit_price: "", currency: "EUR", qty: "", moq: "", payment_terms: "", lead_time_days: "", certifications: "", notes: "" });

const COLS = [
  { k: "supplier", label: "Lieferant", w: "min-w-[140px]", type: "text" },
  { k: "unit_price", label: "Stückpreis", w: "w-28", type: "number" },
  { k: "currency", label: "Währung", w: "w-20", type: "text" },
  { k: "qty", label: "Menge", w: "w-24", type: "number" },
  { k: "moq", label: "MOQ", w: "w-24", type: "number" },
  { k: "payment_terms", label: "Zahlungsziel", w: "w-32", type: "text" },
  { k: "lead_time_days", label: "Lieferzeit (Tage)", w: "w-28", type: "number" },
  { k: "certifications", label: "Zertifikate", w: "min-w-[120px]", type: "text" },
  { k: "notes", label: "Notiz", w: "min-w-[120px]", type: "text" },
];

export default function QuoteComparison() {
  const [title, setTitle] = useState("");
  const [offers, setOffers] = useState([emptyOffer(), emptyOffer()]);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/srm/quote-comparisons?limit=20").then((r) => setHistory(r.data || [])).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const setCell = (i, k, v) => setOffers((p) => p.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)));
  const addRow = () => setOffers((p) => [...p, emptyOffer()]);
  const delRow = (i) => setOffers((p) => (p.length <= 2 ? p : p.filter((_, idx) => idx !== i)));

  const run = async () => {
    const valid = offers.filter((o) => o.supplier.trim());
    if (valid.length < 2) { toast.error("Bitte mindestens zwei Lieferantenangebote mit Namen erfassen."); return; }
    setRunning(true); setResult(null);
    try {
      const r = await api.post("/srm/quote-comparison", {
        title: title.trim(),
        offers: valid.map((o) => ({
          supplier: o.supplier, unit_price: o.unit_price === "" ? null : Number(o.unit_price),
          currency: o.currency, qty: o.qty === "" ? null : Number(o.qty),
          moq: o.moq === "" ? null : Number(o.moq), payment_terms: o.payment_terms,
          lead_time_days: o.lead_time_days === "" ? null : Number(o.lead_time_days),
          certifications: o.certifications, notes: o.notes,
        })),
      });
      setResult(r.data);
      toast.success("Vergleich erstellt");
      loadHistory();
    } catch (e) { toast.error(errDetail(e)); }
    setRunning(false);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="quote-comparison-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center">
          <GitCompareArrows className="h-5 w-5 text-[#F5A623]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1B2A4A]">KI-Angebotsvergleich</h1>
          <p className="text-sm text-gray-500">Lieferantenangebote erfassen und von der KI gegenüberstellen lassen</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <label className="text-xs text-gray-500 block mb-1">Titel (optional)</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z. B. Etikettenrollen Q3 2026"
          className="mb-4 max-w-md" data-testid="quote-comparison-title" />

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-gray-400">
                {COLS.map((c) => <th key={c.k} className="py-2 pr-2 font-medium">{c.label}</th>)}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o, i) => (
                <tr key={i} data-testid={`quote-offer-row-${i}`}>
                  {COLS.map((c) => (
                    <td key={c.k} className="py-1 pr-2">
                      <input type={c.type} value={o[c.k]} onChange={(e) => setCell(i, c.k, e.target.value)}
                        data-testid={`quote-offer-${i}-${c.k}`}
                        className={`h-9 rounded-md border border-gray-300 px-2 text-sm ${c.w}`} />
                    </td>
                  ))}
                  <td className="py-1">
                    <button onClick={() => delRow(i)} disabled={offers.length <= 2}
                      className="text-gray-400 hover:text-red-500 disabled:opacity-30 p-1" data-testid={`quote-offer-del-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center gap-3 mt-4">
          <Button variant="outline" size="sm" onClick={addRow} data-testid="quote-add-offer-btn">
            <Plus className="h-4 w-4 mr-1" />Angebot hinzufügen
          </Button>
          <Button onClick={run} disabled={running} data-testid="quote-compare-run-btn"
            className="bg-[#F5A623] hover:bg-[#e0951a] text-[#1B2A4A] font-semibold ml-auto">
            {running ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Vergleich starten
          </Button>
        </div>
      </div>

      {result && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="quote-comparison-result">
          <div className="font-semibold text-[#1B2A4A] mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#F5A623]" />KI-Empfehlung{result.title ? ` — ${result.title}` : ""}
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{result.result}</p>
        </div>
      )}

      {history.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="quote-comparison-history">
          <div className="font-semibold text-[#1B2A4A] mb-3">Frühere Vergleiche</div>
          <div className="divide-y divide-gray-100">
            {history.map((h) => (
              <button key={h.id} onClick={() => setResult(h)} className="w-full text-left py-2.5 hover:bg-gray-50 rounded px-2 flex items-center justify-between"
                data-testid={`quote-history-${h.id}`}>
                <span className="text-sm text-[#1B2A4A] font-medium">{h.title || "Angebotsvergleich"}</span>
                <span className="text-xs text-gray-400">{(h.offers || []).length} Angebote · {fmtDate(h.created_at)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
